import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  viewingAsUserId: string | null; // User ID when viewing as an account manager
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setViewingAs: (userId: string | null) => void;
  getEffectiveUserId: () => string | null; // Returns the user ID to use for queries
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Initialize viewingAsUserId from localStorage synchronously if possible
  const [viewingAsUserId, setViewingAsUserId] = useState<string | null>(() => {
    // Try to get from localStorage on initial mount
    // We'll update this once we know the user ID
    return null;
  });

  // Load viewingAsUserId from localStorage when user is available
  useEffect(() => {
    if (user?.id) {
      const stored = localStorage.getItem(`viewingAs_${user.id}`);
      if (stored) {
        setViewingAsUserId(stored);
      } else {
        setViewingAsUserId(null);
      }
    } else {
      setViewingAsUserId(null);
    }
  }, [user?.id]);

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    try {
      // Clear the local user state first
      setUser(null);
      setViewingAsUserId(null);
      // Use a simpler sign out that doesn't require an active session
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      // If there's any error, log it but don't throw
      console.warn('Unexpected error during sign out:', error);
    }
  };

  const setViewingAs = (userId: string | null) => {
    setViewingAsUserId(userId);
    if (user?.id) {
      if (userId) {
        localStorage.setItem(`viewingAs_${user.id}`, userId);
      } else {
        localStorage.removeItem(`viewingAs_${user.id}`);
      }
    }
  };

  const getEffectiveUserId = (): string | null => {
    return viewingAsUserId || user?.id || null;
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        loading, 
        viewingAsUserId,
        signIn, 
        signUp, 
        signOut,
        setViewingAs,
        getEffectiveUserId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 